import { useState } from "react";
import { AlertTriangle, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { HAZARD_META } from "./HazardLayer";
import { errorMessage } from "@/lib/errors";

const TYPES = ["police", "accident", "traffic", "danger", "road_closed", "hazard"];

/**
 * HazardReportButton — floating CTA + modal for dropping a community
 * hazard pin at a given (lat,lng). Renders a small button; on tap shows
 * a 6-tile type picker; on selection, posts to /chauffeur/hazards.
 *
 * Props:
 *  • lat / lng: where to drop the pin (usually driver/passenger's live loc)
 *  • compact: if true, render as a smaller chip-style button
 *  • onCreated(hazard): callback after success
 */
export default function HazardReportButton({ lat, lng, compact = false, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [pickedType, setPickedType] = useState(null);

  const disabled = !Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0);

  const submit = async () => {
    if (!pickedType) {
      toast.error("Pick a hazard type");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/chauffeur/hazards", {
        type: pickedType,
        lat,
        lng,
        note: note.trim() || null,
      });
      toast.success(
        res.data?.deduped
          ? "Hazard confirmed — others have flagged it too"
          : "Hazard reported — thanks for keeping drivers safe"
      );
      setOpen(false);
      setNote("");
      setPickedType(null);
      onCreated?.(res.data);
    } catch (err) {
      toast.error(errorMessage(err, "Could not report hazard"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="hazard-report-btn"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={disabled ? "Drop a pin first" : "Report a hazard"}
        className={
          compact
            ? "inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-body uppercase tracking-wider rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            : "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-body uppercase tracking-wider rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25 disabled:opacity-30 transition-colors"
        }
      >
        <AlertTriangle size={12} /> Report hazard
      </button>

      {open && (
        <div data-testid="hazard-modal"
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !busy && setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-luxury-black border border-gold/30 rounded-lg p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold">Community Safety</p>
                <h3 className="font-heading text-lg text-white mt-1">Report a hazard</h3>
                <p className="text-[11px] text-white/50 mt-1">Auto-expires in 4 h. Other users can confirm or clear it.</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white"><X size={18} /></button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {TYPES.map((t) => {
                const meta = HAZARD_META[t];
                const active = pickedType === t;
                return (
                  <button
                    key={t}
                    data-testid={`hazard-type-${t}`}
                    onClick={() => setPickedType(t)}
                    className={`flex flex-col items-center gap-1 px-2 py-3 rounded border text-[11px] uppercase tracking-wider transition-colors ${
                      active
                        ? "border-gold bg-gold/15 text-white"
                        : "border-white/15 text-white/70 hover:border-white/30 hover:bg-white/5"
                    }`}
                  >
                    <span className="text-xl">{meta.emoji}</span>
                    {meta.label}
                  </button>
                );
              })}
            </div>

            <div>
              <label className="text-[11px] font-body uppercase tracking-wider text-white/50">Note (optional)</label>
              <textarea
                data-testid="hazard-note-input"
                value={note}
                maxLength={140}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Brief detail — e.g. radar gun at JFK Bridge"
                className="mt-1 w-full bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white placeholder-white/30 focus:border-gold/60 focus:outline-none"
                rows={2}
              />
              <p className="text-[10px] text-white/30 text-right">{note.length}/140</p>
            </div>

            <button data-testid="hazard-submit-btn" onClick={submit} disabled={busy || !pickedType}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gold text-luxury-black text-xs font-body uppercase tracking-wider hover:bg-gold/90 disabled:opacity-40 disabled:cursor-not-allowed rounded">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
              {busy ? "Reporting…" : "Post hazard"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
